/**
 * `PaidMediaConnectMetaAdsCard` — Task 141 (IC-3 FE wiring) +
 * Task 145 (IC-4 connected-state branch).
 *
 * Drives two surfaces on the client paid-media tab:
 *
 * 1. **Connect CTA** (Task 141, no-account-yet case). The OAuth-write
 *    surface that bounces the user to the Meta-hosted authorize page
 *    via the IC-3 `start-integration-connection-oauth` EF. Strict
 *    gating — four checks, in order, encapsulated in the pure
 *    `shouldRenderConnectMetaAdsCard(me, clientId, enabled)` function
 *    below so the unit tests cover the full decision:
 *      0. **`env.ic3MetaOauthUiEnabled`** — build-time UI flag.
 *         Default OFF; see `apps/web/src/env.ts` for the operator
 *         follow-up required before flipping it true.
 *      1. `me` loaded.
 *      2. `hasOnlyClientCategoryRoles(me)` — strict: not sysadmin,
 *         at least one role, EVERY role's category is `'client'`
 *         (no mixed / platform / influencer side-roles). The shared
 *         `isClientOnly` helper is intentionally NOT used here
 *         because it would admit mixed users.
 *      3. `hasClientAssignment(me, clientId)` — active assignment.
 *
 * 2. **Connected state** (Task 145, IC-4). When the parent passes
 *    an `accounts` list that contains an active Meta `ad_account`
 *    linked to an `integration_connection` for the current
 *    `clientId`, the card renders a quiet "تم ربط حساب إعلانات Meta"
 *    badge INSTEAD of the connect CTA. The connected-state branch:
 *      - Runs BEFORE the strict CTA gate.
 *      - Gates on `hasClientAssignment(me, clientId)` (so cross-
 *        client probes don't render a connected state).
 *      - Does NOT require `env.ic3MetaOauthUiEnabled` — observing
 *        an already-issued connection should not depend on the
 *        OAuth-write flag.
 *      - Reads the `accounts` list passed in by the parent
 *        (`PaidMediaPage`). It does NOT re-fetch `list-ad-accounts`.
 *
 * Hard rules (this component MUST NOT):
 *   - Call Meta Graph API directly. All Meta API calls happen
 *     server-side inside `complete-integration-connection-oauth`.
 *   - Write to `ad_accounts`. The card only READS the passed-in
 *     list for the connected-state branch (Task 145 reads, not
 *     writes — the IC-4 RPC owns the write path under service-role).
 *   - Touch `social_connections` or the publishing OAuth surface
 *     (IC-1 §2).
 *   - Request any platform-mutation scope. The IC-3 start EF sends
 *     `scope=ads_read` ONLY (IC-1 §12).
 */

import { useCallback, useState } from 'react';
import { isApiError } from '../../api/client';
import { startIntegrationConnectionOAuth } from '../../api/endpoints';
import { env } from '../../env';
import { hasClientAssignment } from '../../permissions';
import { useMeContext } from '../../context/MeContextProvider';
import type { AdAccountRow, MeContext } from '../../api/types';

/**
 * Strict client-only gate (Codex Step A round 1 P1).
 *
 * `isClientOnly(me)` in `permissions.ts` only excludes `system_admin`
 * + `agency`-category roles. The `RoleCategory` enum has four values
 * (`'platform' | 'agency' | 'client' | 'influencer'`), so a user with
 * a `platform` or `influencer` role PLUS a `client` role would slip
 * past `isClientOnly`. The IC-3 EF's server-side gate
 * (`hasAnyNonClientActiveRole`) rejects ANY non-client-category role,
 * so the FE gate must match EXACTLY — defense in depth, and to keep
 * the CTA from rendering when the EF would 403.
 *
 * Returns `true` only when the caller (a) is not a sysadmin, (b) has
 * at least one active role, and (c) EVERY role's category is `'client'`.
 *
 * Exported for direct test access.
 */
export function hasOnlyClientCategoryRoles(me: MeContext): boolean {
  if (me.system_admin) return false;
  if (!me.roles || me.roles.length === 0) return false;
  for (const r of me.roles) {
    if (r.category !== 'client') return false;
  }
  return true;
}

/**
 * Single end-to-end gate function (Codex Step A round 2 P2). The
 * component returns `null` iff this returns `false`. Extracting the
 * gate to a pure function lets the unit tests cover the FULL gating
 * decision (not just the role-category helper in isolation) without
 * needing a DOM-rendering test harness.
 *
 * The four guards in order:
 *   0. **Build-time UI flag (Task 141 round 4).** The `enabled`
 *      parameter (sourced from `env.ic3MetaOauthUiEnabled`) is the
 *      FIRST gate. Default OFF — see `apps/web/src/env.ts` for the
 *      operator follow-up required before flipping this to true.
 *      Checked first so a disabled deploy doesn't even pay for the
 *      `me`/role/assignment reads.
 *   1. `me` loaded.
 *   2. Strict client-only role posture (matches IC-3 EF gate exactly).
 *   3. Active client-assignment row for this workspace's clientId.
 *
 * Exported for direct test access.
 */
export function shouldRenderConnectMetaAdsCard(
  me: MeContext | null,
  clientId: string,
  enabled: boolean,
): boolean {
  if (!enabled) return false;
  if (!me) return false;
  if (!hasOnlyClientCategoryRoles(me)) return false;
  if (!hasClientAssignment(me, clientId)) return false;
  return true;
}

/**
 * SessionStorage key under which the FE stashes the `redirect_back_to`
 * path keyed by state. Belt-and-suspenders: the IC-3 start EF also
 * persists the path on the `integration_oauth_states` row, and the
 * callback EF echoes it back in the success body. This stash is the
 * fallback path the callback page uses if the success body omits it
 * (e.g. on a typed error mid-flow that still wants to bounce the
 * user home).
 *
 * NEVER stash the state token itself. The state is the CSRF secret;
 * persistence outside the server-side row is a leak surface.
 */
export const IC3_REDIRECT_BACK_TO_SESSION_KEY_PREFIX = 'kmi:ic3:redirect-back-to:';

/**
 * Task 191 — SessionStorage key under which the FE stashes the OAuth
 * PROVIDER (`'meta'` | `'snapchat'`) keyed by state. The integration
 * callback page (`/auth/integration-oauth-callback`) is shared across
 * providers, so it reads this hint to render provider-aware copy
 * (heading / picker prompt / error) BEFORE the success response is in
 * (which itself also carries `connection.provider`). Provider is NOT a
 * secret. NEVER stash the state token itself.
 */
export const IC_OAUTH_PROVIDER_SESSION_KEY_PREFIX = 'kmi:ic:oauth-provider:';

interface ConnectState {
  readonly status: 'idle' | 'loading' | 'error';
  readonly errorCode?: string;
  readonly errorMessageAr?: string;
}

interface PaidMediaConnectMetaAdsCardProps {
  readonly clientId: string;
  /**
   * Task 145 (IC-4) — the ad_accounts list for this workspace. When
   * an active Meta ad_account exists for `clientId` AND it carries
   * a non-null `integration_connection_id`, the card renders a
   * connected-state badge INSTEAD of the connect CTA. Defaults to
   * empty for safe pre-IC-4 callers.
   */
  readonly accounts?: ReadonlyArray<AdAccountRow>;
}

/**
 * Task 145 (IC-4) — true iff `accounts` contains at least one active
 * Meta ad-account for `clientId` that's already linked to an
 * integration_connection. Pure helper; exported for direct test access.
 *
 * Task 146 — `integration_connection_id` must be a non-empty STRING
 * (a real uuid). The original Task 145 form used `!== null`, which
 * admits `undefined` (`undefined !== null` is `true`). The
 * production `list-ad-accounts` EF at version 24 (pre-PR-#121
 * deploy gap) omits the field from the wire row, so the FE sees
 * `undefined`. The looser check mis-classified those rows as
 * "linked" and rendered the connected-state — which then collided
 * with the hooks-order regression and blanked the page. Tightening
 * to a string-truthy check makes the FE silent on the
 * connected-state branch until the EF redeploy actually surfaces
 * the link id, which is the correct UX either way.
 */
export function hasActiveLinkedMetaAccount(
  accounts: ReadonlyArray<AdAccountRow>,
  clientId: string,
): boolean {
  return accounts.some((a) => isLinkedActiveMetaRow(a, clientId));
}

/**
 * Task 152 — shared row matcher and single source of truth for "a linked
 * active Meta ad-account for `clientId`": an ACTIVE, non-archived Meta
 * ad-account that carries a string-truthy `integration_connection_id`
 * (a real linked credential, Task 146). Every predicate/finder below
 * builds on this so the link-id check lives in exactly one place.
 */
function isLinkedActiveMetaRow(a: AdAccountRow, clientId: string): boolean {
  return (
    a.client_id === clientId &&
    a.platform === 'meta_ads' &&
    a.status === 'active' &&
    a.archived_at === null &&
    typeof a.integration_connection_id === 'string' &&
    a.integration_connection_id.length > 0
  );
}

/** Task 152 — a PRESENT (string) IC status that is not `active`. */
function isKnownNonActiveStatus(a: AdAccountRow): boolean {
  const status = a.integration_connection_status;
  return typeof status === 'string' && status !== 'active';
}

/**
 * Task 145 (IC-4) + Task 146 — pick the first matching active linked
 * Meta ad-account row from `accounts`. Returns `null` when none.
 */
function findActiveLinkedMetaAccount(
  accounts: ReadonlyArray<AdAccountRow>,
  clientId: string,
): AdAccountRow | null {
  return accounts.find((a) => isLinkedActiveMetaRow(a, clientId)) ?? null;
}

/**
 * Task 152 — pick the first linked active Meta ad-account whose backing
 * `integration_connection` status is a KNOWN non-active value. Used to
 * name the broken account in the reauthorization-required card. Returns
 * `null` when none.
 */
function findReauthRequiredMetaAccount(
  accounts: ReadonlyArray<AdAccountRow>,
  clientId: string,
): AdAccountRow | null {
  return (
    accounts.find((a) => isLinkedActiveMetaRow(a, clientId) && isKnownNonActiveStatus(a)) ?? null
  );
}

/**
 * Task 152 — EXISTENTIAL: true iff AT LEAST ONE linked active Meta
 * ad-account for `clientId` has a backing `integration_connection` that
 * is `active` (the state that makes the green "متصل" badge eligible). A
 * missing / undefined / null status never counts, so the green badge
 * degrades safely during a wire-version skew (FE deployed before the EF
 * surfaces the field). Checking EVERY matching row (not just the first)
 * means a sibling row can't mask the answer (Codex Step A P2). Exported
 * for direct test access.
 */
export function isLinkedMetaConnectionActive(
  accounts: ReadonlyArray<AdAccountRow>,
  clientId: string,
): boolean {
  return accounts.some(
    (a) => isLinkedActiveMetaRow(a, clientId) && a.integration_connection_status === 'active',
  );
}

/**
 * Task 152 — EXISTENTIAL: true iff AT LEAST ONE linked active Meta
 * ad-account for `clientId` has a backing `integration_connection` whose
 * status is a KNOWN non-active value (`reauthorization_required` /
 * `revoked` / `expired`). Checking EVERY matching row (not just the
 * first) means a broken account is never masked by a healthy sibling
 * (Codex Step A P2). A missing / undefined / null status does NOT
 * trigger this — it falls through to the connect gate rather than
 * showing a false "re-link" prompt during a wire-version skew. This is
 * the predicate that drives the "إعادة الربط مطلوبة" warning state.
 * Exported for direct test access.
 */
export function hasReauthRequiredMetaAccount(
  accounts: ReadonlyArray<AdAccountRow>,
  clientId: string,
): boolean {
  return accounts.some((a) => isLinkedActiveMetaRow(a, clientId) && isKnownNonActiveStatus(a));
}

export function PaidMediaConnectMetaAdsCard({
  clientId,
  accounts = [],
}: PaidMediaConnectMetaAdsCardProps) {
  const { me } = useMeContext();
  const [connect, setConnect] = useState<ConnectState>({ status: 'idle' });

  const handleConnect = useCallback(async () => {
    setConnect({ status: 'loading' });
    try {
      // Compute redirect_back_to from the current path. The callback
      // page reads it from the success body (preferred) or from
      // sessionStorage (fallback) and navigates the user there after
      // the flow completes.
      const redirectBackTo =
        typeof window !== 'undefined' ? window.location.pathname + window.location.search : null;

      const data = await startIntegrationConnectionOAuth({
        input: {
          client_id: clientId,
          provider: 'meta',
          redirect_back_to: redirectBackTo,
        },
      });

      // Stash by state so the callback page can recover the path even
      // if the success body's `connection.redirect_back_to` is null
      // (e.g. some error paths still want a sane home).
      try {
        if (typeof window !== 'undefined' && data.state && redirectBackTo) {
          window.sessionStorage.setItem(
            `${IC3_REDIRECT_BACK_TO_SESSION_KEY_PREFIX}${data.state}`,
            redirectBackTo,
          );
        }
        // Task 191 — stash the provider so the shared callback page can
        // render provider-aware copy. Guarded on state only (no
        // redirect_back_to dependency).
        if (typeof window !== 'undefined' && data.state) {
          window.sessionStorage.setItem(
            `${IC_OAUTH_PROVIDER_SESSION_KEY_PREFIX}${data.state}`,
            'meta',
          );
        }
      } catch {
        // SessionStorage failures (private mode, quota) are non-fatal —
        // the success body will carry the path + provider. Ignore.
      }

      // Top-level navigation to the provider's authorize page. The
      // user signs into Meta there; Meta redirects to META_REDIRECT_URI
      // with `?state=&code=`; the FE callback page picks up from there.
      window.location.href = data.authorize_url;
    } catch (err: unknown) {
      if (isApiError(err)) {
        const fieldMarker = readFirstFieldMarker(err.body.fields);
        const wireDetail = fieldMarker ?? err.code;
        setConnect({
          status: 'error',
          errorCode: wireDetail,
          errorMessageAr: describeStartOAuthError(wireDetail),
        });
        return;
      }
      setConnect({
        status: 'error',
        errorCode: 'network_error',
        errorMessageAr: 'تعذّر الاتصال بالخدمة. تحقّق من اتصالك بالإنترنت وحاول مرة أخرى.',
      });
    }
  }, [clientId]);

  // Task 152 — reauthorization-required state, checked BEFORE the green
  // connected branch so a broken connection always wins over a healthy
  // sibling. A linked active Meta ad_account exists for this workspace
  // but its backing integration_connection is no longer `active` (e.g.
  // the Task 150 token-key cutover flipped it to
  // `reauthorization_required`). Render a warning state — NEVER the green
  // "متصل" badge — and keep the account visible. The WARNING is gated
  // like the connected state on the active client-assignment (NOT on the
  // OAuth-write flag): observing a broken connection must not depend on
  // the write flag, and assigned agency leadership has status visibility
  // (IC-1 UX). The re-link BUTTON, however, is gated on the SAME full
  // client-only predicate the connect CTA uses
  // (`shouldRenderConnectMetaAdsCard`, Codex Step A P2) — it triggers the
  // OAuth-write surface, which is client-context-only (the IC-3 EF 403s
  // an agency actor) and stays hidden until the operator's paid-media
  // redirect-URI follow-up lands (see apps/web/src/env.ts).
  //
  // Task 146 POSITION INVARIANT — this and the connected branch MUST sit
  // AFTER every hook call (`useMeContext`, `useState`, `useCallback`) so
  // the hook count is constant across renders.
  if (
    me !== null &&
    hasClientAssignment(me, clientId) &&
    hasReauthRequiredMetaAccount(accounts, clientId)
  ) {
    const account = findReauthRequiredMetaAccount(accounts, clientId);
    return (
      <div className="card" style={{ padding: 22 }}>
        <div className="sec-title">
          <h2>إعادة ربط حساب إعلانات Meta</h2>
          <span className="pill pill-review">إعادة الربط مطلوبة</span>
        </div>
        <p className="tiny" style={{ color: 'var(--muted)', marginTop: 6 }}>
          {account?.account_name ? (
            <>
              انتهت صلاحية ربط الحساب <strong>{account.account_name}</strong>. أعد ربط الحساب
              لاستئناف قراءة بيانات الحملات. لن يتأثر أي ربط آخر.
            </>
          ) : (
            <>انتهت صلاحية ربط حساب إعلانات Meta. أعد الربط لاستئناف قراءة بيانات الحملات.</>
          )}
        </p>
        {shouldRenderConnectMetaAdsCard(me, clientId, env.ic3MetaOauthUiEnabled) ? (
          <div style={{ marginTop: 16 }}>
            <button
              type="button"
              className="btn-primary"
              onClick={handleConnect}
              disabled={connect.status === 'loading'}
            >
              {connect.status === 'loading' ? 'جاري التحويل إلى Meta…' : 'إعادة ربط الحساب'}
            </button>
          </div>
        ) : (
          <p className="tiny" style={{ color: 'var(--muted)', marginTop: 12 }}>
            إعادة الربط تتم من حساب العميل.
          </p>
        )}
        {connect.status === 'error' ? (
          <div
            className="banner banner-error"
            role="alert"
            style={{ marginTop: 12 }}
            data-error-code={connect.errorCode}
          >
            {connect.errorMessageAr}
          </div>
        ) : null}
      </div>
    );
  }

  // Task 145 (IC-4) — connected-state branch. When an active Meta
  // ad_account already exists for this workspace, carries a non-empty
  // integration_connection_id (string, not undefined), AND its backing
  // integration_connection is `active` (Task 152), render the
  // connected-state card INSTEAD of the connect CTA. Gated on the
  // active-link check + a defensive client-assignment check so a
  // cross-client probe doesn't render the connected state for a
  // workspace the caller isn't on. Deliberately NOT gated on
  // env.ic3MetaOauthUiEnabled — an active connection deserves to be
  // observable regardless of whether the OAuth-write flag is on.
  //
  // Task 146 — POSITION INVARIANT. This early return MUST sit
  // AFTER every hook call (`useMeContext`, `useState`, `useCallback`)
  // so the hook count is constant across renders. The original
  // Task 145 form put this branch between `useState` and
  // `useCallback`, which violated React's Rules of Hooks: on the
  // render where the condition flipped from false → true,
  // `useCallback` was no longer called, and React unmounted the
  // tree (blanking the page). See `tasks/146-...md`.
  if (
    me !== null &&
    hasClientAssignment(me, clientId) &&
    hasActiveLinkedMetaAccount(accounts, clientId) &&
    isLinkedMetaConnectionActive(accounts, clientId)
  ) {
    const active = findActiveLinkedMetaAccount(accounts, clientId);
    return (
      <div className="card" style={{ padding: 22 }}>
        <div className="sec-title">
          <h2>تم ربط حساب إعلانات Meta</h2>
          <span className="pill pill-active">متصل</span>
        </div>
        <p className="tiny" style={{ color: 'var(--muted)', marginTop: 6 }}>
          {active?.account_name ? (
            <>
              الحساب: <strong>{active.account_name}</strong>
              {active?.account_external_id ? (
                <>
                  {' '}
                  ·{' '}
                  <code style={{ direction: 'ltr', unicodeBidi: 'isolate' }}>
                    act_{active.account_external_id}
                  </code>
                </>
              ) : null}
              {active?.currency_code ? <> · {active.currency_code}</> : null}
            </>
          ) : (
            <>تمت قراءة بيانات الحملات والمجموعات الإعلانية أدناه.</>
          )}
        </p>
      </div>
    );
  }

  // Gating delegated to the pure `shouldRenderConnectMetaAdsCard`
  // function so the test suite can cover the full decision in one
  // place. The function encapsulates all four guards (env feature
  // flag, me loaded, strict client-only role posture, active client-
  // assignment) — a future refactor that drops one of them would
  // fail the unit tests for shouldRenderConnectMetaAdsCard.
  if (!shouldRenderConnectMetaAdsCard(me, clientId, env.ic3MetaOauthUiEnabled)) return null;

  return (
    <div className="card" style={{ padding: 22 }}>
      <div className="sec-title">
        <h2>اربط حساب إعلانات Meta</h2>
      </div>
      <p className="tiny" style={{ color: 'var(--muted)', marginTop: 6 }}>
        اربط حساب إعلاناتك على Meta (Facebook / Instagram) لقراءة بيانات الحملات. سنطلب صلاحية{' '}
        <code style={{ direction: 'ltr', unicodeBidi: 'isolate' }}>ads_read</code> فقط — لا نطلب أي
        صلاحية للنشر أو التعديل أو إنشاء الإعلانات.
      </p>
      <div style={{ marginTop: 16 }}>
        <button
          type="button"
          className="btn-primary"
          onClick={handleConnect}
          disabled={connect.status === 'loading'}
        >
          {connect.status === 'loading' ? 'جاري التحويل إلى Meta…' : 'اربط الحساب الآن'}
        </button>
      </div>
      {connect.status === 'error' ? (
        <div
          className="banner banner-error"
          role="alert"
          style={{ marginTop: 12 }}
          data-error-code={connect.errorCode}
        >
          {connect.errorMessageAr}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Map a typed start-OAuth wire marker (or the generic wire code) to
 * Arabic operator-friendly copy. Unknown markers fall back to a
 * generic "try again" message. The wire codes / markers come from
 * the IC-3 start EF authority chain.
 */
export function describeStartOAuthError(code: string): string {
  switch (code) {
    case 'agency_oauth_path_forbidden':
      return 'هذا الإجراء متاح فقط لمستخدمي العميل (وليس مستخدمي الوكالة). تواصل مع فريق العميل لإكمال الربط.';
    case 'provider_unsupported':
    case 'provider_required':
      return 'مزوّد الإعلان غير مدعوم في هذه النسخة. حالياً يتم دعم Meta فقط.';
    case 'unauthenticated':
      return 'انتهت جلستك. سجّل دخولك من جديد ثم حاول مرة أخرى.';
    case 'permission_denied':
      return 'لا تملك صلاحية بدء الربط لهذا العميل.';
    case 'not_found':
      return 'العميل غير موجود أو غير متاح لحسابك.';
    case 'internal_error':
    case 'network_error':
      return 'تعذّر الاتصال بالخدمة. حاول مرة أخرى بعد قليل.';
    default:
      return 'تعذّر بدء عملية الربط. حاول مرة أخرى بعد قليل.';
  }
}

/**
 * Inspect a `KmiApiError.body.fields` map and return the first
 * `_body[0]` marker if present, or any other field's first value
 * (used to surface validation tag for the operator copy mapper).
 */
function readFirstFieldMarker(
  fields: Readonly<Record<string, ReadonlyArray<string>>> | undefined,
): string | undefined {
  if (!fields) return undefined;
  const bodyMarkers = fields['_body'];
  if (bodyMarkers && bodyMarkers.length > 0) return bodyMarkers[0];
  for (const v of Object.values(fields)) {
    if (v && v.length > 0) return v[0];
  }
  return undefined;
}
